import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DashboardLayout, { menuItems } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Upload, Loader2, Image, Mail, SidebarClose, Eye, EyeOff, Printer } from 'lucide-react';
import { PostersManager } from '../../components/Posters';
import { AutomationsPanel } from '../../components/AutomationsPanel';
import { MaintenancePanel } from '../../components/MaintenancePanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SIGNATURE = `<p style="margin:0 0 4px 0;"><strong style="color:#064E3B;">Nurulhuda Quranic School</strong></p>
<p style="margin:0 0 4px 0;color:#4B5563;">Empowering students through Quranic education and Islamic knowledge.</p>
<p style="margin:0;"><a href="mailto:info@nhq.ie" style="color:#064E3B;">info@nhq.ie</a> &nbsp;|&nbsp; <a href="https://nhq.ie" style="color:#064E3B;">nhq.ie</a></p>`;

const SettingsPage = () => {
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingSignature, setSavingSignature] = useState(false);
    const [savingSidebar, setSavingSidebar] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const logoInputRef = useRef(null);
    const [form, setForm] = useState({ school_name: '', email: '', address: '', phone_numbers: '' });
    const [signature, setSignature] = useState('');
    const [reportHeader, setReportHeader] = useState('');
    const [reportFooter, setReportFooter] = useState('');
    const [savingReport, setSavingReport] = useState(false);
    // hidden paths per role — { manager: [...], teacher: [...], parent: [...] }
    const [sidebarVisibility, setSidebarVisibility] = useState({ manager: [], teacher: [], parent: [] });

    useEffect(() => { fetchSettings(); }, []);

    const fetchSettings = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/settings`, { withCredentials: true });
            setSettings(res.data);
            setForm({
                school_name: res.data.school_name || '',
                email: res.data.email || '',
                address: res.data.address || '',
                phone_numbers: (res.data.phone_numbers || []).join(', ')
            });
            setSignature(res.data.email_signature || '');
            setReportHeader(res.data.report_header || '');
            setReportFooter(res.data.report_footer || '');
            const vis = res.data.sidebar_visibility || {};
            setSidebarVisibility({
                manager: Array.isArray(vis.manager) ? vis.manager : [],
                teacher: Array.isArray(vis.teacher) ? vis.teacher : [],
                parent: Array.isArray(vis.parent) ? vis.parent : [],
            });
        } catch { toast.error('Failed to load settings'); }
        finally { setLoading(false); }
    };

    const toggleItem = (role, path) => {
        setSidebarVisibility(prev => {
            const hidden = new Set(prev[role] || []);
            if (hidden.has(path)) hidden.delete(path); else hidden.add(path);
            return { ...prev, [role]: Array.from(hidden) };
        });
    };

    const handleSaveSidebar = async () => {
        setSavingSidebar(true);
        try {
            await axios.put(`${API_URL}/api/settings`, { sidebar_visibility: sidebarVisibility }, { withCredentials: true });
            toast.success('Sidebar visibility saved');
        } catch { toast.error('Failed to save sidebar settings'); }
        finally { setSavingSidebar(false); }
    };

    const handleSave = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            await axios.put(`${API_URL}/api/settings`, {
                school_name: form.school_name,
                email: form.email,
                address: form.address,
                phone_numbers: form.phone_numbers.split(',').map(p => p.trim()).filter(Boolean)
            }, { withCredentials: true });
            toast.success('Settings saved');
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    const handleSaveSignature = async () => {
        setSavingSignature(true);
        try {
            await axios.put(`${API_URL}/api/settings`, { email_signature: signature }, { withCredentials: true });
            toast.success('Email signature saved');
        } catch { toast.error('Failed to save signature'); }
        finally { setSavingSignature(false); }
    };

    const handleSaveReport = async () => {
        setSavingReport(true);
        try {
            await axios.put(`${API_URL}/api/settings`, { report_header: reportHeader, report_footer: reportFooter }, { withCredentials: true });
            toast.success('Report header/footer saved');
        } catch { toast.error('Failed to save'); }
        finally { setSavingReport(false); }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0]; if (!file) return;
        setUploadingLogo(true);
        const formData = new FormData(); formData.append('file', file);
        try {
            const res = await axios.post(`${API_URL}/api/settings/logo`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            setSettings(prev => ({ ...prev, logo_url: res.data.logo_url }));
            toast.success('Logo uploaded! Refresh the page to see it everywhere.');
        } catch { toast.error('Failed to upload logo'); }
        finally { setUploadingLogo(false); if (logoInputRef.current) logoInputRef.current.value = ''; }
    };

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div></div></DashboardLayout>;

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="settings-page">
                <div><h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">School Settings</h1><p className="text-[#4B5563]">Manage school information and branding</p></div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Logo Upload */}
                    <Card className="card-surface">
                        <CardHeader><CardTitle className="font-['Outfit'] text-lg flex items-center gap-2"><Image className="h-5 w-5 text-[#064E3B]" />School Logo</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center gap-6">
                                <div className="h-24 w-24 rounded-xl border-2 border-dashed border-[#D1D5DB] flex items-center justify-center overflow-hidden bg-[#FAF9F6]">
                                    {settings.logo_url ? <img src={settings.logo_url} alt="Logo" className="h-full w-full object-contain" /> : <Image className="h-8 w-8 text-[#4B5563]/30" />}
                                </div>
                                <div>
                                    <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload" data-testid="logo-upload-input" />
                                    <Button variant="outline" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} data-testid="upload-logo-btn">
                                        {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                                        Upload Logo
                                    </Button>
                                    <p className="text-xs text-[#4B5563] mt-2">PNG, JPG or SVG. Appears on login page, sidebar, and footer.</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* School Info */}
                    <Card className="card-surface">
                        <CardHeader><CardTitle className="font-['Outfit'] text-lg flex items-center gap-2"><SettingsIcon className="h-5 w-5 text-[#064E3B]" />School Information</CardTitle></CardHeader>
                        <CardContent>
                            <form onSubmit={handleSave} className="space-y-4">
                                <div className="space-y-2"><Label>School Name</Label><Input value={form.school_name} onChange={e => setForm({...form, school_name: e.target.value})} /></div>
                                <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
                                <div className="space-y-2"><Label>Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
                                <div className="space-y-2"><Label>Phone Numbers (comma separated)</Label><Input value={form.phone_numbers} onChange={e => setForm({...form, phone_numbers: e.target.value})} placeholder="+353 1 234 5678, +353 1 876 5432" /></div>
                                <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save Settings</Button>
                            </form>
                        </CardContent>
                    </Card>
                </div>

                {/* Email Signature */}
                <Card className="card-surface">
                    <CardHeader>
                        <CardTitle className="font-['Outfit'] text-lg flex items-center gap-2"><Mail className="h-5 w-5 text-[#064E3B]" />Email Signature</CardTitle>
                        <p className="text-xs text-[#4B5563] mt-1">Appears at the bottom of payment receipts and system emails. The school logo is added automatically above your signature. HTML is supported (links with <code>&lt;a&gt;</code>, colors with <code>style=&quot;color:...&quot;</code>, bold, italics, lists).</p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>HTML Signature</Label>
                                <textarea
                                    className="w-full min-h-[220px] rounded-md border border-[#D1D5DB] p-3 text-sm font-mono focus:ring-2 focus:ring-[#064E3B] focus:border-transparent"
                                    value={signature}
                                    onChange={e => setSignature(e.target.value)}
                                    placeholder={'<p><strong>Nurulhuda Quranic School</strong></p>\n<p><a href="mailto:info@nhq.ie">info@nhq.ie</a></p>'}
                                    data-testid="email-signature-textarea"
                                />
                                <div className="flex gap-2">
                                    <Button type="button" variant="outline" size="sm" onClick={() => setSignature(DEFAULT_SIGNATURE)} data-testid="load-default-signature">Load Default</Button>
                                    <Button type="button" onClick={handleSaveSignature} disabled={savingSignature} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="save-signature-btn">{savingSignature && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save Signature</Button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Preview <span className="text-xs text-[#6B7280] font-normal">— rendered in a sandboxed iframe; scripts cannot execute</span></Label>
                                <div className="min-h-[220px] rounded-md border border-[#E5E7EB] bg-white p-4">
                                    {settings.logo_url && <div className="pb-2 mb-2 border-b border-[#F3F4F6]"><img src={settings.logo_url} alt="School Logo" style={{maxHeight:60,maxWidth:180,objectFit:'contain'}} /></div>}
                                    <iframe
                                        title="signature-preview"
                                        sandbox=""
                                        srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#1F2937;}</style></head><body>${signature || '<span style="color:#9CA3AF;font-style:italic;">Signature preview will appear here…</span>'}</body></html>`}
                                        style={{width:'100%', minHeight:'160px', border:'none'}}
                                    />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <MaintenancePanel />
                <AutomationsPanel />

                <PostersManager />

                {/* Report Header & Footer (used on all printable A4 reports) */}
                <Card className="card-surface">
                    <CardHeader>
                        <CardTitle className="font-['Outfit'] text-lg flex items-center gap-2"><Printer className="h-5 w-5 text-[#064E3B]" />Report Header &amp; Footer</CardTitle>
                        <p className="text-xs text-[#4B5563] mt-1">
                            HTML for the header and footer that appear on all printable A4 reports (participants roster, groups, rooms).
                            The school logo (uploaded above) is automatically placed to the left of the header. Leave a field blank to use the default.
                        </p>
                    </CardHeader>
                    <CardContent className="space-y-5">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Header HTML</Label>
                                <textarea
                                    className="w-full min-h-[110px] rounded-md border border-[#D1D5DB] p-3 text-sm font-mono focus:ring-2 focus:ring-[#064E3B] focus:border-transparent"
                                    value={reportHeader}
                                    onChange={e => setReportHeader(e.target.value)}
                                    placeholder={'<h1 style="margin:0;color:#064E3B;">Nurulhuda Quranic School</h1>\n<p style="margin:0;font-size:12px;color:#6B7280;">Participants Roster</p>'}
                                    data-testid="report-header-textarea"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Footer HTML</Label>
                                <textarea
                                    className="w-full min-h-[110px] rounded-md border border-[#D1D5DB] p-3 text-sm font-mono focus:ring-2 focus:ring-[#064E3B] focus:border-transparent"
                                    value={reportFooter}
                                    onChange={e => setReportFooter(e.target.value)}
                                    placeholder={'<p style="margin:0;">info@nhq.ie · +353 000 000 000 · www.nhq.ie</p>'}
                                    data-testid="report-footer-textarea"
                                />
                            </div>
                        </div>
                        <div className="rounded-md border border-[#E5E7EB] bg-white p-4">
                            <p className="text-xs text-[#6B7280] mb-2">Preview (rendered in a sandboxed iframe):</p>
                            <iframe
                                title="report-preview"
                                sandbox=""
                                srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#1F2937;padding:12px;}hr{border:none;border-top:2px solid #064E3B;margin:8px 0;}.footer{margin-top:20px;padding-top:6px;border-top:1px solid #E5E7EB;font-size:11px;color:#6B7280;text-align:center;}</style></head><body>
                                    <div style="display:flex;gap:12px;align-items:center;">
                                        ${settings.logo_url ? `<img src="${settings.logo_url}" style="max-height:50px;max-width:120px;">` : ''}
                                        <div style="flex:1;">${reportHeader || '<h1 style="margin:0;color:#064E3B;">Nurulhuda Quranic School</h1>'}</div>
                                    </div>
                                    <hr/>
                                    <p style="color:#9CA3AF;font-style:italic;">— report body appears here —</p>
                                    <div class="footer">${reportFooter || '<p style=\"margin:0;\">info@nhq.ie</p>'}</div>
                                </body></html>`}
                                style={{ width: '100%', minHeight: '220px', border: '1px solid #E5E7EB', borderRadius: 4 }}
                            />
                        </div>
                        <div className="flex justify-end">
                            <Button type="button" onClick={handleSaveReport} disabled={savingReport} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="save-report-header-footer-btn">
                                {savingReport && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save Report Header/Footer
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Sidebar Visibility */}
                <Card className="card-surface">
                    <CardHeader>
                        <CardTitle className="font-['Outfit'] text-lg flex items-center gap-2"><SidebarClose className="h-5 w-5 text-[#064E3B]" />Sidebar Visibility</CardTitle>
                        <p className="text-xs text-[#4B5563] mt-1">Toggle which sidebar items are visible for each role. Turning a switch <strong>off</strong> hides that item from that role&rsquo;s sidebar. Super Admin always sees every item.</p>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {['manager', 'teacher', 'parent'].map(role => {
                                const roleItems = menuItems[role] || [];
                                const validPaths = new Set(roleItems.map(i => i.path));
                                // Filter out orphan hidden paths that no longer exist in the current menu
                                const hiddenSet = new Set((sidebarVisibility[role] || []).filter(p => validPaths.has(p)));
                                const hiddenCount = hiddenSet.size;
                                return (
                                    <div key={role} className="rounded-lg border border-[#E5E7EB] p-3" data-testid={`sidebar-role-${role}`}>
                                        <div className="flex items-center justify-between mb-2 pb-2 border-b border-[#F3F4F6]">
                                            <h4 className="font-semibold capitalize text-[#1F2937]">{role}</h4>
                                            <span className="text-xs text-[#4B5563]">{roleItems.length - hiddenCount} / {roleItems.length} visible</span>
                                        </div>
                                        <div className="space-y-1 max-h-[340px] overflow-y-auto pr-1">
                                            {roleItems.map(item => {
                                                const hidden = hiddenSet.has(item.path);
                                                return (
                                                    <div key={item.path} className="flex items-center justify-between text-sm py-1">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            {hidden ? <EyeOff className="h-3.5 w-3.5 text-[#9CA3AF] flex-shrink-0" /> : <Eye className="h-3.5 w-3.5 text-[#10B981] flex-shrink-0" />}
                                                            <span className={hidden ? 'text-[#9CA3AF] line-through truncate' : 'text-[#1F2937] truncate'}>{item.label}</span>
                                                        </div>
                                                        <Switch
                                                            checked={!hidden}
                                                            onCheckedChange={() => toggleItem(role, item.path)}
                                                            data-testid={`toggle-${role}-${item.label.replace(/\s+/g,'-').toLowerCase()}`}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-4 flex justify-end">
                            <Button onClick={handleSaveSidebar} disabled={savingSidebar} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="save-sidebar-visibility-btn">{savingSidebar && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save Sidebar Visibility</Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Parent Portal Toggles */}
                <Card className="card-surface">
                    <CardHeader>
                        <CardTitle className="font-['Outfit'] text-lg">Parent Portal</CardTitle>
                        <p className="text-xs text-[#4B5563] mt-1">Additional parent-visible features controlled from here.</p>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-start justify-between gap-4 py-2 border-b border-[#F3F4F6]" data-testid="show-previous-years-toggle-row">
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-[#1F2937]">Show Previous Years to Parents</p>
                                <p className="text-xs text-[#4B5563] mt-0.5">When enabled, parents see a "Previous Years" page with their archived students, receipts and totals.</p>
                            </div>
                            <Switch
                                checked={!!settings?.show_previous_years_to_parents}
                                onCheckedChange={async (v) => {
                                    try {
                                        await axios.put(`${API_URL}/api/settings`, { show_previous_years_to_parents: v }, { withCredentials: true });
                                        setSettings(s => ({ ...(s || {}), show_previous_years_to_parents: v }));
                                        toast.success(v ? 'Enabled Previous Years for parents' : 'Hid Previous Years from parents');
                                    } catch { toast.error('Failed to update setting'); }
                                }}
                                data-testid="toggle-show-previous-years"
                            />
                        </div>

                        <div className="pt-4 space-y-3" data-testid="current-year-dates">
                            <div>
                                <p className="text-sm font-medium text-[#1F2937]">Current-year receipt cutoff dates</p>
                                <p className="text-xs text-[#4B5563] mt-0.5">Only receipts on/after these dates count towards each parent&apos;s &quot;this year&quot; balance and Paid/Unpaid status.</p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs text-[#6B7280]">Tuition & Registration start</label>
                                    <input
                                        type="date"
                                        className="mt-1 w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm"
                                        value={settings?.current_year_tuition_start || ''}
                                        onChange={(e) => setSettings(s => ({ ...(s || {}), current_year_tuition_start: e.target.value }))}
                                        onBlur={async (e) => {
                                            try {
                                                await axios.put(`${API_URL}/api/settings`, { current_year_tuition_start: e.target.value }, { withCredentials: true });
                                                toast.success('Saved');
                                            } catch { toast.error('Failed to save'); }
                                        }}
                                        data-testid="tuition-start-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-[#6B7280]">Renewal start</label>
                                    <input
                                        type="date"
                                        className="mt-1 w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm"
                                        value={settings?.current_year_renewal_start || ''}
                                        onChange={(e) => setSettings(s => ({ ...(s || {}), current_year_renewal_start: e.target.value }))}
                                        onBlur={async (e) => {
                                            try {
                                                await axios.put(`${API_URL}/api/settings`, { current_year_renewal_start: e.target.value }, { withCredentials: true });
                                                toast.success('Saved');
                                            } catch { toast.error('Failed to save'); }
                                        }}
                                        data-testid="renewal-start-input"
                                    />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </DashboardLayout>
    );
};

export default SettingsPage;
